/**
 * On-site interaction (CTA tap) tracking — shared by the jobsite and the booking
 * page. One delegated, capture-phase click listener emits an event whenever a
 * tracked CTA is tapped, so individual components wire nothing: they either use a
 * `tel:`/`sms:`/`mailto:`/maps link (inferred automatically) or carry a
 * `data-track` attribute (for CTAs that have no such href, e.g. a "Text us" button
 * that opens a dialog).
 *
 * These are intent signals, not leads. Best-effort: never throws, never blocks.
 */

/** Canonical client vocabulary. The server column is open, so new actions can be
 *  added here without a migration; keep the server's EventBodySchema regex in mind.
 *  The overview only aggregates the first four; the /links page's actions (book,
 *  quote, review, gallery, social, link) are stored for later surfacing. */
const INTERACTION_ACTIONS = [
    "call",
    "text",
    "directions",
    "email",
    "book",
    "quote",
    "review",
    "gallery",
    "social",
    "link",
  ] as const,
  /** Owner-preview mode. Shared so the pageview and interaction trackers agree —
   *  the owner previewing their own site must never pollute real metrics. */
  isPreviewMode = (): boolean => {
    try {
      return new URLSearchParams(window.location.search).get("preview") === "true";
    } catch {
      return false;
    }
  },
  /**
   * Random id that also works outside a secure context.
   *
   * `crypto.randomUUID` is only defined on HTTPS/localhost. A jobsite reached over
   * plain `http://` — which is what Google Business Profile still links to — gets
   * `undefined` here, and an unguarded call throws during hydration and takes the
   * whole rendered page down with it. Analytics must never do that.
   */
  randomId = (): string => {
    try {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }
    } catch {
      /* Fall through to the non-crypto id */
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  },
  /**
   * Stable per-tab session id under `key`, minted on first call. Falls back to an
   * ephemeral id when sessionStorage is unavailable (private mode, embedded webviews).
   */
  getSessionId = (key: string): string => {
    try {
      const existing = sessionStorage.getItem(key);
      if (existing) {
        return existing;
      }
      const id = randomId();
      sessionStorage.setItem(key, id);
      return id;
    } catch {
      return randomId();
    }
  },
  /** Read UTM params off the current URL (shared with the pageview tracker). */
  utmFromUrl = (): {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
  } => {
    try {
      const p = new URLSearchParams(window.location.search),
        pick = (k: string) => p.get(k) || undefined;
      return {
        utm_source: pick("utm_source"),
        utm_medium: pick("utm_medium"),
        utm_campaign: pick("utm_campaign"),
        utm_term: pick("utm_term"),
        utm_content: pick("utm_content"),
      };
    } catch {
      return {};
    }
  };
const actionFromHref = (href: string | null): InteractionAction | null => {
    if (!href) {
      return null;
    }
    const h = href.trim().toLowerCase();
    if (h.startsWith("tel:")) {
      return "call";
    }
    if (h.startsWith("sms:")) {
      return "text";
    }
    if (h.startsWith("mailto:")) {
      return "email";
    }
    if (
      h.startsWith("https://maps.") ||
      h.includes("google.com/maps") ||
      h.includes("maps.app.goo.gl") ||
      h.includes("goo.gl/maps")
    ) {
      return "directions";
    }
    return null;
  },
  /** SendBeacon with a keepalive-fetch fallback. Beacon sends a typed Blob so the
   *  server's JSON body parser accepts it (a text/plain beacon would be dropped). */
  sendBeaconJson = (url: string, body: Record<string, unknown>): void => {
    const json = JSON.stringify(body);
    try {
      const blob = new Blob([json], { type: "application/json" });
      if (typeof navigator !== "undefined" && navigator.sendBeacon?.(url, blob)) {
        return;
      }
    } catch {
      /* Fall through to fetch */
    }
    try {
      void fetch(url, {
        body: json,
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        method: "POST",
      }).catch(() => {});
    } catch {
      /* Never throw from analytics */
    }
  },
  DEDUPE_WINDOW_MS = 1500,
  /**
   * Install the delegated click tracker. Returns a cleanup function.
   * Resolves the action from an explicit `data-track` first, else infers it from
   * the nearest anchor's href. Dedupes identical (action,label) taps within a short
   * window to absorb double-taps. No-ops in preview mode and in non-DOM contexts.
   */
  installInteractionTracking = (config: InteractionTrackerConfig): () => void => {
    if (typeof document === "undefined") {
      return () => {};
    }
    const recent = new Map<string, number>(),

      handler = (event: Event) => {
        if (isPreviewMode()) {
          return;
        }
        const target = event.target as Element | null,
          el = target?.closest?.("[data-track], a[href]") as HTMLElement | null;
        if (!el) {
          return;
        }

        const explicit = el.getAttribute("data-track"),
          anchorHref = (el.closest("a"))?.getAttribute("href") ?? null,
          action = (explicit as InteractionAction | null) ?? actionFromHref(anchorHref);
        if (!action || !INTERACTION_ACTIONS.includes(action)) {
          return;
        }

        const label = el.getAttribute("data-track-label") || undefined,

          key = `${action}:${label ?? ""}`,
          now = Date.now();
        if (now - (recent.get(key) ?? 0) < DEDUPE_WINDOW_MS) {
          return;
        }
        recent.set(key, now);

        sendBeaconJson(config.endpoint, {
          ...config.extraBody,
          action,
          label,
          path: config.getPath ? config.getPath() : window.location.pathname,
          referrer: (typeof document !== "undefined" && document.referrer) || undefined,
          sessionId: config.sessionId,
        });
      };

    document.addEventListener("click", handler, true);
    return () => {
      document.removeEventListener("click", handler, true);
    };
  };
type InteractionAction = (typeof INTERACTION_ACTIONS)[number];

interface InteractionTrackerConfig {
  /** Full URL to POST interaction events to. */
  endpoint: string;
  /** Per-tab session id (shared with the pageview tracker). */
  sessionId: string;
  /** Extra fields merged into every event body (e.g. `{ slug }` for booking). */
  extraBody?: Record<string, unknown>;
  /** Defaults to `location.pathname`. */
  getPath?: () => string;
}

export type { InteractionAction, InteractionTrackerConfig };
export {
  INTERACTION_ACTIONS,
  isPreviewMode,
  randomId,
  getSessionId,
  utmFromUrl,
  sendBeaconJson,
  installInteractionTracking,
};
